import qs from 'qs';
import SV from 'server-vars';
import { getBuyerToken } from 'dibs-cookie-jar';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { getPrerenderedCheckoutState } from '../prerender/prerenderedCheckoutStore';
import { deferActionIfInBackground } from '../prerender/deferActionIfInBackground';
import { disableBeforeUnload } from '../shared/hooks/useBeforeUnload';
import { CHECKOUT_QUERY_PARAMS } from '../constants/checkoutConstants';
import { getCheckoutTrigger } from './checkoutTrackingTyped';

export function getParsedQueryKeys() {
    const { params } = getPrerenderedCheckoutState();
    const queryParams = qs.parse(window.location.search.substr(1));

    return { ...queryParams, ...params };
}

function getPathFromUrl(pathName) {
    const pathArray = window.location.pathname.split('/') || [];
    return pathArray.filter(path => path.indexOf(pathName) > -1)[0] || '';
}

export function getKountUrl(itemId, vertical) {
    if (!itemId || !vertical) {
        return null;
    }
    const kountParams = Object.assign({ itemId, vertical }, getBuyerToken(document.cookie));
    return ['/soa/ecom-4/3/transaction/data.gif', qs.stringify(kountParams)].join('?');
}

export function getParamFromUrl(param) {
    const params = getParsedQueryKeys();
    return params.hasOwnProperty(param) ? params[param] : undefined;
}

export function getItemIdFromUrl() {
    const idStr = 'id-';
    const idPath = getPathFromUrl(idStr) || '';
    return getParamFromUrl('itemId') || idPath.replace(idStr, '');
}

export function getCheckoutParam(param) {
    return getParamFromUrl(param) || SV.get(`checkoutPostData.${param}`);
}

export function getCheckoutUrl(itemId) {
    const baseUrl = '/checkout/id-';
    const itemPk = itemId || getItemIdFromUrl();

    if (!itemPk) {
        throw new Error(
            'getCheckoutUrl method expects a Backbone model item or to have it in the url.'
        );
    }
    return baseUrl.concat(itemPk);
}

export function isUserComesFromEmail() {
    return (
        getParamFromUrl('utm_medium') === 'email' ||
        getCheckoutParam('userComesFromEmail') === 'true'
    );
}

export function isUserComesFromAcceptedOffer() {
    return getParamFromUrl('acceptOffer') === 'true' || getParamFromUrl('fullPrice') === 'true';
}

export function isUserNegotiating() {
    return !!getParamFromUrl('offerAmount');
}

// use wrapper for easier spying when testing
export function redirect(url) {
    deferActionIfInBackground(() => {
        // we actually want to go somewhere now, make sure
        // the warning is turned off
        disableBeforeUnload();
        window.location.href = handleLocaleUrl(url, GLOBAL_CLIENT_ONLY_LOCALE);
    });
}

export function goToMessageCenter(buyerId) {
    const itemId = getItemIdFromUrl();
    if (itemId && buyerId) {
        redirect(`/inbox/buyer/${buyerId}/item/${itemId}`);
    } else {
        redirect('/');
    }
}

function getCheckoutConfirmationUrl({ transactionId, itemId, paymentAuthorizationId, guestToken }) {
    const origin = window.location.origin;
    const url = new URL(`/checkout/confirmation/id-${itemId}/${transactionId}/`, origin);

    if (paymentAuthorizationId) {
        url.searchParams.set(
            CHECKOUT_QUERY_PARAMS.PAYMENT_AUTHORIZATION_ID,
            paymentAuthorizationId
        );
        if (guestToken) {
            url.searchParams.set(CHECKOUT_QUERY_PARAMS.GUEST_TOKEN, guestToken);
        }
    }

    return url.toString().replace(origin, '');
}

export function getCheckoutPaymentReturnUrl({
    transactionId,
    itemId,
    paymentAuthorizationId,
    guestToken,
    submissionId,
}) {
    const origin = window.location.origin;

    const checkoutUrl = new URL(getCheckoutUrl(itemId), origin);
    checkoutUrl.searchParams.append(CHECKOUT_QUERY_PARAMS.SUBMISSION_ID, submissionId);

    const returnUrl = new URL(
        getCheckoutConfirmationUrl({ transactionId, itemId, paymentAuthorizationId, guestToken }),
        origin
    );
    returnUrl.searchParams.append(
        CHECKOUT_QUERY_PARAMS.PAYMENT_FAILURE_REDIRECT_URL,
        checkoutUrl.toString().replace(origin, '')
    );

    return returnUrl.toString();
}

export function goToBundleConfirmation({ bundleId }) {
    const origin = window.location.origin;
    const url = new URL(`/checkout/confirmation/bundle/${bundleId}/`, origin);
    const bundleConfirmationUrl = url.toString().replace(origin, '');
    redirect(bundleConfirmationUrl);
}

export function goToConfirmation({ transactionId, itemId, paymentAuthorizationId, guestToken }) {
    redirect(
        getCheckoutConfirmationUrl({
            transactionId,
            itemId: itemId || getItemIdFromUrl(),
            paymentAuthorizationId,
            guestToken,
        })
    );
}

export function goToOMT(transactionId) {
    let url = '/orders/';
    if (transactionId) {
        url = url.concat(`order/${transactionId}`);
    }
    redirect(url);
}

export function goToItemCategory(browseUrl) {
    const url = browseUrl || '/';
    redirect(url);
}

export function goToMyCart() {
    redirect('/my/cart/');
}

export function reloadCheckout(isNegotiableOrder, isUserComesFromEmailArgument) {
    const params = { ...getParsedQueryKeys() };

    // MONEY-1455 - save checkout trigger in query param for tracking
    if (!params.triggerType) {
        params.triggerType = getCheckoutTrigger(isNegotiableOrder, isUserComesFromEmailArgument);
    }
    const checkoutUrl = getCheckoutUrl();
    const paramsUrl = qs.stringify(params);
    const url = checkoutUrl.concat(`?${paramsUrl}`);

    redirect(url);
}
